import { useState } from 'react';
import { Send } from 'lucide-react';
import type { Order } from '@/apis/orders';
import { useAddWarehouseNote, useUserSummaries } from '@/hooks/useWarehouse';

interface Props {
  order: Order;
  canAct?: boolean;
}

export function NotesSection({ order, canAct = true }: Props) {
  const [noteText, setNoteText] = useState('');
  const addNote = useAddWarehouseNote(order.id);
  const notes = order.metadata?.warehouseNotes ?? [];
  const summariesById = useUserSummaries(notes.map((n) => n.author));

  const authorDisplay = (userId: string) => {
    const summary = summariesById.get(userId);
    return summary ? `${summary.name} (${summary.roleLabel})` : userId;
  };

  const handleSend = () => {
    if (!noteText.trim()) return;
    addNote.mutate({ text: noteText.trim() }, { onSuccess: () => setNoteText('') });
  };

  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold text-black">Notes &amp; Comments</h2>

      <div className="flex flex-col gap-2 rounded-2xl border border-smoke bg-smoke1/50 p-4">
        {notes.length === 0 ? (
          <p className="py-2 text-sm text-lightGray">No notes yet.</p>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="rounded-xl bg-white px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-black">{authorDisplay(note.author)}</span>
                <span className="text-xs font-normal text-lightGray">
                  {new Date(note.createdAt).toLocaleString('en-US')}
                </span>
              </div>
              <p className="mt-1 text-sm font-normal text-dark2">{note.text}</p>
            </div>
          ))
        )}
      </div>

      <div className="mt-3 rounded-2xl border border-smoke">
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Type a note..."
          rows={2}
          disabled={!canAct}
          className="w-full resize-none rounded-2xl border-0 bg-transparent px-4 py-3 text-sm text-black placeholder:text-lightGray outline-none disabled:cursor-not-allowed disabled:opacity-60"
        />
        <div className="flex items-center justify-end border-t border-smoke px-4 py-2">
          <button
            type="button"
            onClick={handleSend}
            disabled={!canAct || addNote.isPending || !noteText.trim()}
            className="flex items-center gap-1.5 rounded-full bg-primary-s500 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Send size={13} />
            {addNote.isPending ? 'Sending…' : 'Send Note'}
          </button>
        </div>
      </div>
    </section>
  );
}
